import { useCallback, useState } from 'react'

import type { CustomFieldSchema } from '@tribeplatform/gql-client/types'
import {
  useListFilters,
  mergeInlineFilters,
  convertToRawInlineFilter,
  FilterAppearances,
  type ListFilter,
  type RawInlineFilter,
  type InlineFilter,
  type RawFilter,
  useInlineFilters,
} from '@tribeplatform/react-components/Filters'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import {
  FieldSortableItemVariant,
  FieldsSortableList,
  GenericSettings,
} from '@tribeplatform/react-components/Views'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import type { SetInlineFilterProperty } from '../types.js'
import { filterAppearancesFactory } from '../utils/selectItems.js'
import { InlineFilterItemSettings } from './InlineFilterItemSettings.js'

interface Props {
  entityListFilters: ListFilter[]
  staticFilters: RawFilter[]
  customFields: CustomFieldSchema[]
  allowInlineFilters: boolean
  filterAppearance: FilterAppearances
  rawInlineFilters: RawInlineFilter[]
  setInlineFilters: (inlineFilters: RawInlineFilter[]) => void
  setFilterAppearance: (value: FilterAppearances) => void
  setAllowInlineFilters: (value: boolean) => void
  goBack: () => void
}

export const InlineFilterSettings = ({
  entityListFilters,
  staticFilters,
  customFields,
  allowInlineFilters,
  filterAppearance,
  rawInlineFilters,
  setInlineFilters,
  setFilterAppearance,
  setAllowInlineFilters,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const [selectedInlineFilterItemId, setSelectedInlineFilterItemId] =
    useState<string>(null)
  const filterAppearances = useStaticIntl(filterAppearancesFactory)
  const { filters, allFilters } = useListFilters({
    entityListFilters,
    customFields,
    staticFilters,
  })
  const { processedInlineFilters, availableInlineFilters } = useInlineFilters({
    entityListFilters,
    filters,
    allFilters,
    rawInlineFilters,
  })
  const mergedInlineFilters = mergeInlineFilters(
    rawInlineFilters,
    availableInlineFilters,
  )

  const toggleInlineFilter = (id: string) => {
    const newItems = mergedInlineFilters.map(item =>
      item.id === id ? { ...item, enabled: !item.enabled } : item,
    )

    setInlineFilters(convertToRawInlineFilter(newItems))
  }

  const setInlineFilterProperty = useCallback<SetInlineFilterProperty>(
    (key, value) => {
      const newInlineFilters = mergedInlineFilters.map(inlineFilter => {
        if (inlineFilter.id === selectedInlineFilterItemId) {
          const newInlineFilter: InlineFilter = {
            ...inlineFilter,
            [key]: value,
          }

          return newInlineFilter
        }

        return inlineFilter
      })

      setInlineFilters(convertToRawInlineFilter(newInlineFilters))
    },
    [mergedInlineFilters, selectedInlineFilterItemId, setInlineFilters],
  )

  return (
    <>
      <GenericSettings
        title={$t({
          defaultMessage: 'Inline filtering',
          id: 'ViewSettings.Filters.InlineFiltering',
        })}
        description={
          <T
            defaultMessage="Enable user filtering based on the criteria below. Multiple criteria will be grouped together."
            id="ViewSettings.Filters.InlineFilterSettings.HelperText"
          />
        }
        goBack={goBack}
      >
        <div className="space-y-5">
          <Toggle
            title={<T defaultMessage="Enabled" id="Generics.Enabled" />}
            checked={allowInlineFilters}
            onChange={value => {
              setAllowInlineFilters(!!value)
            }}
          />
          <FormControl.Select
            name="filterAppearance"
            label={<T defaultMessage="Appearance" id="Generics.Appearance" />}
            value={filterAppearance}
            onChange={(value: FilterAppearances) => {
              setFilterAppearance(value)
            }}
            items={filterAppearances}
            disabled={!allowInlineFilters}
          />
          <div className="space-y-1">
            <FormControl.Label>
              <T defaultMessage="Fields" id="Generics.Fields" />
            </FormControl.Label>
            <FieldsSortableList
              items={mergedInlineFilters}
              onReorder={newItems =>
                setInlineFilters(convertToRawInlineFilter(newItems))
              }
              onToggle={toggleInlineFilter}
              onClick={inlineFilterId =>
                setSelectedInlineFilterItemId(inlineFilterId)
              }
              disabled={!allowInlineFilters}
              variant={FieldSortableItemVariant.SWITCH}
            />
          </div>
        </div>
      </GenericSettings>
      {selectedInlineFilterItemId && (
        <InlineFilterItemSettings
          inlineFilter={mergedInlineFilters.find(
            inlineFilter => inlineFilter.id === selectedInlineFilterItemId,
          )}
          processedInlineFilter={processedInlineFilters.find(
            filter => filter.id === selectedInlineFilterItemId,
          )}
          setProperty={setInlineFilterProperty}
          goBack={() => setSelectedInlineFilterItemId(null)}
        />
      )}
    </>
  )
}
